import React, { useState, useEffect } from "react";
import { ref, onValue, push, set, update, remove } from "firebase/database";
import { db } from "./firebase";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logoPath from "./assets/x_math_logo.png";
import { formatCourseId } from "./utils/formatCourse";

const ATTENDANCE_STATUSES = ["Absent", "Attended", "Online"];

const AdminAttendance = () => {
    const [courses, setCourses] = useState([]);
    const [activeCourse, setActiveCourse] = useState("");
    const [students, setStudents] = useState({});
    const [classes, setClasses] = useState({});
    const [newClassName, setNewClassName] = useState("");
    const [newClassDate, setNewClassDate] = useState("");
    const [selectedClassId, setSelectedClassId] = useState("");
    const [attendanceDraft, setAttendanceDraft] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Fetch courses
    useEffect(() => {
        const coursesRef = ref(db, "courses");
        return onValue(coursesRef, (snapshot) => {
            if (snapshot.exists()) {
                setCourses(Object.keys(snapshot.val()));
            }
        });
    }, []);

    // Fetch students and classes for active course
    useEffect(() => {
        if (!activeCourse) return;
        setLoading(true);
        const studentsRef = ref(db, `courses/${activeCourse}/students`);
        const attendanceRef = ref(db, `courses/${activeCourse}/attendance`);
        const unsubStudents = onValue(studentsRef, (snap) => {
            if (snap.exists()) {
                const studentsData = snap.val();
                // Sort students alphabetically by name
                const sortedStudents = Object.fromEntries(
                    Object.entries(studentsData).sort(([, a], [, b]) =>
                        (a.name || '').localeCompare(b.name || '')
                    )
                );
                setStudents(sortedStudents);
            } else {
                setStudents({});
            }
        });
        const unsubAttendance = onValue(attendanceRef, (snap) => {
            setClasses(snap.exists() ? snap.val() : {});
            setLoading(false);
        });
        return () => { unsubStudents(); unsubAttendance(); };
    }, [activeCourse]);

    // Add new class
    const handleAddClass = async () => {
        if (!newClassName.trim() || !newClassDate) return;
        const attendanceRef = ref(db, `courses/${activeCourse}/attendance`);
        const newClassRef = push(attendanceRef);
        const classId = newClassRef.key;
        // Only save className and date under the class node
        await set(newClassRef, {
            className: newClassName.trim(),
            date: newClassDate
        });
        // Save each student's attendance under their own node
        for (const sid of Object.keys(students)) {
            await set(ref(db, `courses/${activeCourse}/students/${sid}/attendance/${classId}`), {
                className: newClassName.trim(),
                date: newClassDate,
                status: "Absent"
            });
        }
        setNewClassName("");
        setNewClassDate("");
    };

    // When a class is selected, load its attendance into draft from the students' nodes
    useEffect(() => {
        if (!selectedClassId || !classes[selectedClassId]) {
            setAttendanceDraft({});
            return;
        }
        // Gather attendance from each student's node
        const draft = {};
        Object.entries(students).forEach(([sid, s]) => {
            const att = s.attendance && s.attendance[selectedClassId];
            draft[sid] = att ? att.status : "Absent";
        });
        setAttendanceDraft(draft);
    }, [selectedClassId, classes, students]);

    // Change attendance status in draft
    const handleAttendanceChange = (studentId, status) => {
        setAttendanceDraft(draft => ({ ...draft, [studentId]: status }));
    };

    // Save attendance changes: only update under each student's node
    const handleSaveAttendance = async () => {
        if (!selectedClassId) return;
        for (const [sid, status] of Object.entries(attendanceDraft)) {
            await update(ref(db, `courses/${activeCourse}/students/${sid}/attendance/${selectedClassId}`), {
                status
            });
        }
    };

    // Delete class and all associated attendance records
    const handleDeleteClass = async () => {
        if (!selectedClassId || !classes[selectedClassId]) return;

        const classObj = classes[selectedClassId];
        const confirmMessage = `Are you sure you want to delete the class "${classObj.className} (${classObj.date})"?\n\nThis will permanently delete:\n- The class record\n- All attendance records for this class\n\nThis action cannot be undone.`;

        if (!window.confirm(confirmMessage)) return;

        try {
            // Delete the class from attendance node
            await remove(ref(db, `courses/${activeCourse}/attendance/${selectedClassId}`));

            // Delete attendance records from each student's node
            const deletePromises = Object.keys(students).map(sid =>
                remove(ref(db, `courses/${activeCourse}/students/${sid}/attendance/${selectedClassId}`))
            );

            await Promise.all(deletePromises);

            // Clear selection
            setSelectedClassId("");
            setAttendanceDraft({});

            alert("Class deleted successfully");
        } catch (error) {
            alert("Error deleting class: " + error.message);
        }
    };

    // PDF Export for selected class
    const exportToPDF = () => {
        if (!selectedClassId || !classes[selectedClassId]) return;
        const classObj = classes[selectedClassId];
        const doc = new jsPDF();
        const img = new window.Image();
        img.src = logoPath;
        img.onload = () => {
            const logoW = 42;
            const logoH = logoW * (img.naturalHeight / img.naturalWidth);
            doc.addImage(img, "PNG", 10, 8, logoW, logoH);
            doc.setFontSize(20);
            doc.text("Class Attendance Report", 60, 20);
            doc.setFontSize(12);
            doc.text(`Course: ${activeCourse}`, 60, 30);
            doc.text(`Class: ${classObj.className} (${classObj.date})`, 60, 36);

            // Sort students alphabetically for PDF
            const sortedStudents = Object.entries(students).sort(([, a], [, b]) =>
                (a.name || '').localeCompare(b.name || '')
            );

            const tableData = sortedStudents.map(([sid, s]) => [
                sid,
                s.name || sid,
                attendanceDraft[sid] || "Absent"
            ]);

            autoTable(doc, {
                head: [["Student ID", "Name", "Status"]],
                body: tableData,
                startY: 45,
                theme: 'grid',
                styles: {
                    fontSize: 10,
                    cellPadding: 5,
                },
                columnStyles: {
                    0: { cellWidth: 25 },
                    1: { cellWidth: 'auto' },
                    2: { cellWidth: 30 }
                }
            });
            doc.save(`${activeCourse}_${classObj.className}_${classObj.date}_attendance.pdf`);
        };
    };

    return (
        <div className="p-4 min-h-screen text-black">
            <h1 className="text-3xl font-bold mb-6">Attendance Management</h1>
            {/* Course selector */}
            <div className="mb-6">
                <label htmlFor="attendance-course" className="block text-sm font-medium text-gray-700 mb-1">Course</label>
                <select
                    id="attendance-course"
                    value={activeCourse}
                    onChange={e => { setActiveCourse(e.target.value); setSelectedClassId(""); }}
                    className="p-2 border rounded-md text-gray-900 min-w-[200px]"
                >
                    <option value="">Select course</option>
                    {courses.map(c => <option key={c} value={c}>{formatCourseId(c)}</option>)}
                </select>
            </div>

            {!activeCourse ? (
                <div className="flex flex-col items-center justify-center py-16 px-6 bg-gradient-to-b from-gray-50 to-white border-2 border-dashed border-gray-200 rounded-2xl shadow-sm">
                    <div className="w-16 h-16 mb-4 rounded-full bg-blue-100 flex items-center justify-center">
                        <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">No course selected</h3>
                    <p className="text-gray-500 text-sm max-w-sm">Please select a course from the dropdown above to view and manage content.</p>
                </div>
            ) : (
                <>
            {/* Add Class */}
            <div className="mb-8 bg-white p-4 rounded-lg shadow-md">
                    <h2 className="text-lg font-semibold mb-2">Add New Class</h2>
                    <div className="flex flex-col sm:flex-row gap-2 items-center">
                        <input
                            type="text"
                            placeholder="Class name"
                            value={newClassName}
                            onChange={e => setNewClassName(e.target.value)}
                            className="p-2 border rounded-md flex-1"
                        />
                        <input
                            type="date"
                            value={newClassDate}
                            onChange={e => setNewClassDate(e.target.value)}
                            className="p-2 border rounded-md flex-1"
                        />
                        <button
                            onClick={handleAddClass}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                        >
                            Add Class
                        </button>
                    </div>
                </div>
            {/* Classes List/Dropdown */}
            {Object.keys(classes).length > 0 && (
                <div className="mb-8">
                    <label className="block mb-2 font-medium">Select Class:</label>
                    <select
                        value={selectedClassId}
                        onChange={e => setSelectedClassId(e.target.value)}
                        className="p-2 border rounded-md"
                    >
                        <option value="">Select class</option>
                        {Object.entries(classes).map(([classId, classObj]) => (
                            <option key={classId} value={classId}>
                                {classObj.className} ({classObj.date})
                            </option>
                        ))}
                    </select>
                </div>
            )}
            {/* Students Table for Selected Class */}
            {selectedClassId && classes[selectedClassId] && (
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="flex justify-between items-center px-6 pt-6">
                        <h2 className="text-lg font-semibold mb-4">
                            {classes[selectedClassId].className} - {classes[selectedClassId].date}
                        </h2>
                        <div className="flex gap-2">
                            <button
                                onClick={exportToPDF}
                                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Export to PDF
                            </button>
                            <button
                                onClick={handleDeleteClass}
                                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                            >
                                Delete Class
                            </button>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Student ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {Object.entries(students)
                                    .sort(([, a], [, b]) => (a.name || '').localeCompare(b.name || '')) // Sort alphabetically
                                    .map(([sid, s], idx) => {
                                        const status = attendanceDraft[sid] || "Absent";
                                        let statusBg = "";
                                        let statusText = "";
                                        if (status === "Attended" || status === "Online") {
                                            statusBg = "bg-green-50";
                                            statusText = "text-green-700";
                                        } else if (status === "Absent") {
                                            statusBg = "bg-red-50";
                                            statusText = "text-red-700";
                                        }
                                        return (
                                            <tr key={sid} className={`hover:bg-blue-50 transition`}>
                                                <td className="px-6 py-3 font-mono text-xs text-gray-500">{sid}</td>
                                                <td className="px-6 py-3">{s.name || sid}</td>
                                                <td className="px-6 py-3">
                                                    <select
                                                        value={status}
                                                        onChange={e => handleAttendanceChange(sid, e.target.value)}
                                                        className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-400 ${statusBg} ${statusText}`}
                                                    >
                                                        <option value="Attended" className="text-green-700 bg-green-50">Attended</option>
                                                        <option value="Online" className="text-green-700 bg-green-50">Online</option>
                                                        <option value="Absent" className="text-red-700 bg-red-50">Absent</option>
                                                    </select>
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                    <div className="px-6 pb-6 pt-4">
                        <button
                            onClick={handleSaveAttendance}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Save Attendance
                        </button>
                    </div>
                </div>
            )}
                </>
            )}
        </div>
    );
};

export default AdminAttendance; 